import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/layout/Sidebar';
import './Projects.css';

const ProjectItem = ({ project, projectRef }) => {
  const { t } = useTranslation();
  
  // Funzione per normalizzare i titoli
  const normalizeTitle = (title) => {
    if (!title) return '';
    return title.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Gestisce entrambi i formati di dati per compatibilità
  const projectTitle = normalizeTitle(project.title || project.titolo);
  const projectDescription = project.description || project.descrizione;
  const projectImages = project.images || (project.immagine ? [project.immagine] : []);
  const projectLocation = project.location;
  const projectYear = project.year;
  const projectSquareMeters = project.squareMeters;
  const projectFeatures = project.features;
  const projectValue = project.projectValue;

  const [mainImage, setMainImage] = useState(projectImages[0]);

  return (
    <div className="project-item" ref={projectRef}>
      <div className="project-image-section">
        <div className="main-image-container">
          <img 
            src={mainImage} 
            className="project-main-image" 
            alt={projectTitle}
            onError={(e) => {
              e.target.src = '/icons/logo-192.png';
            }}
          />
        </div>
        {projectImages.length > 1 && (
          <div className="image-gallery">
            {projectImages.map((image, index) => (
              <div 
                key={index} 
                className={`gallery-thumbnail ${mainImage === image ? 'active' : ''}`}
                onClick={() => setMainImage(image)}
              >
                <img 
                  src={image} 
                  alt={`${projectTitle} - Immagine ${index + 1}`}
                  onError={(e) => {
                    e.target.src = '/icons/logo-192.png';
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="project-info-section">
        <div className="project-header">
          <h2 className="project-title">{projectTitle}</h2>
          <div className="project-meta">
            <span className="project-location">{projectLocation}</span>
            <span className="project-year">{projectYear}</span>
            {projectSquareMeters && (
              <span className="project-size">{projectSquareMeters} m²</span>
            )}
          </div>
        </div>
        <p className="project-description">{projectDescription}</p>
        {projectFeatures && projectFeatures.length > 0 && (
          <div className="project-features">
            {projectFeatures.map((feature, index) => (
              <span key={index} className="feature-item">• {feature}</span>
            ))}
          </div>
        )}
        {projectValue && (
          <div className="project-value">
            <strong>{t('projectValue')}:</strong> € {projectValue.toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('tutti');
  const projectRefs = useRef({});
  const location = useLocation();
  const { t, i18n } = useTranslation();

      useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                const apiUrl = process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com';
                const response = await fetch(`${apiUrl}/api/projects?limit=50&lang=${i18n.language}`);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                
                if (data.success) {
                    const allProjects = data.data;
                    setProjects(allProjects);
                } else {
                    setError('Errore nel caricamento dei progetti');
                }
            } catch (err) {
                setError('Errore di connessione');
                console.error('Errore nel caricamento dei progetti:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [i18n.language]);

  // Gestisce lo scroll al progetto specifico quando viene passato come parametro
  useEffect(() => {
    if (!loading && projects.length > 0) {
      const urlParams = new URLSearchParams(location.search);
      const projectId = urlParams.get('project');
      
      if (projectId && projectRefs.current[projectId]) {
        setTimeout(() => {
          projectRefs.current[projectId].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 1000);
      }
    }
  }, [loading, projects, location.search]);

  const getFilteredProjects = () => {
    let filteredProjects = projects;
    
    // Applica filtro per categoria se non è "tutti"
    if (activeFilter !== 'tutti') {
      // Mappa dei filtri per entrambe le lingue
      const filterMap = {
        'ristrutturazione': i18n.language === 'en' ? 'renovation' : 'ristrutturazione',
        'commerciale': i18n.language === 'en' ? 'commercial' : 'commerciale',
        'residenziale': i18n.language === 'en' ? 'residential' : 'residenziale'
      };
      
      filteredProjects = projects.filter(project => {
        // Controlla sia il campo category che categoria per compatibilità
        const projectCategory = project.category || project.categoria;
        return projectCategory === filterMap[activeFilter];
      });
    }
    
    // Ordina i progetti dal più recente al più vecchio
    return filteredProjects.sort((a, b) => {
      // Prova diversi campi di data in ordine di priorità
      const getProjectDate = (project) => {
        // 1. Data di inizio progetto
        if (project.dataInizio) return new Date(project.dataInizio);
        // 2. Data di fine progetto
        if (project.dataFine) return new Date(project.dataFine);
        // 3. Anno di realizzazione (convertito in data)
        if (project.year) return new Date(project.year, 0, 1); // 1 gennaio dell'anno
        // 4. Data di creazione nel database
        if (project.createdAt) return new Date(project.createdAt);
        // 5. Fallback a data molto vecchia
        return new Date(1900, 0, 1);
      };
      
      const dateA = getProjectDate(a);
      const dateB = getProjectDate(b);
      
      return dateB - dateA; // Ordine decrescente (più recente prima)
    });
  };

  const filteredProjects = getFilteredProjects();

  if (error) {
    return (
      <>
        <Sidebar />
        <div style={{ marginLeft: '80px' }}>
          <div className="container mt-5">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: '80px' }}>
        <div className="projects-container">
          <div className="projects-header">
            <h1 className="main-title">{t('projectsTitle')}</h1>
            <p className="header-description">
              {t('projectsSubtitle')}
            </p>
          </div>

          {/* Filtri */}
          <div className="projects-filters">
            <button 
              className={`filter-btn ${activeFilter === 'tutti' ? 'active' : ''}`}
              onClick={() => setActiveFilter('tutti')}
            >
              {t('allProjects')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'ristrutturazione' ? 'active' : ''}`}
              onClick={() => setActiveFilter('ristrutturazione')}
            >
              {t('buildingRenovation')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'commerciale' ? 'active' : ''}`}
              onClick={() => setActiveFilter('commerciale')}
            >
              {t('retailHotelServices')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'residenziale' ? 'active' : ''}`}
              onClick={() => setActiveFilter('residenziale')}
            >
              {t('interiorRenovation')}
            </button>
          </div>

          {/* Contenuto progetti */}
          <div className="projects-content">
            {loading ? (
              <div className="loading-container">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
                <p>{t('loading')}</p>
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="projects-list">
                {filteredProjects.map((project, index) => (
                  <ProjectItem 
                    key={project._id} 
                    project={project} 
                    projectRef={(el) => {
                      projectRefs.current[project._id] = el;
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="no-projects">
                <h3>{t('error')}</h3>
                <p>{t('error')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Projects; 